import { useEffect } from 'react';
import sal from 'sal.js';

function cleanupSal(e: Event) {
  if (e.currentTarget) {
    const el = e.currentTarget as HTMLElement;
    setTimeout(() => {
      el.removeAttribute('data-sal');
      el.removeAttribute('data-sal-delay');
      el.removeEventListener('transitionend', cleanupSal);
    }, 50);
  }
}

function cleanupSalContainer(container: HTMLElement) {
  const children = container.querySelectorAll('*[data-sal-child]');
  let transitionEndCount = 0;

  function cleanupSalChild(e: Event) {
    transitionEndCount++;
    if (e.currentTarget) {
      const el = e.currentTarget as HTMLElement;
      setTimeout(() => {
        el.removeAttribute('data-sal-child');
        el.removeAttribute('data-sal-delay');
        el.removeEventListener('transitionend', cleanupSalChild);
      }, 50);
    }

    if (transitionEndCount >= children.length) {
      container.removeAttribute('data-sal');
      container.removeAttribute('data-sal-delay');
    }
  }

  children.forEach(el => el.addEventListener('transitionend', cleanupSalChild));
}

/**
 * Initiate sal and add event listeners for cleanup
 */
export function useInitiateSal(dependencyArray: any[]) {
  useEffect(() => {
    // Initiate Sal
    window.requestAnimationFrame(() => {
      sal({ root: null, once: true, threshold: 0.05 });
    });

    // Remove Sal data-attribute from direct Sal elements
    document.querySelectorAll('*[data-sal]:not([data-sal*="-container"])').forEach(el => {
      el.addEventListener('transitionend', cleanupSal);
    });
    // Remove Sal data-attribute from Sal containers
    document.querySelectorAll('*[data-sal*="-container"]').forEach(el => {
      cleanupSalContainer(el as HTMLElement);
    });
  }, dependencyArray);
}

/**
 * Fake the sal functionality by adding the animated class on each render
 * Used e.g. in Storybook.
 */
export function useFakeSal(dependencyArray?: any[]) {
  useEffect(() => {
    const salContainers = document.querySelectorAll('*[data-sal]:not([data-sal*="-container"])');
    const salElements = document.querySelectorAll('*[data-sal*="-container"]');
    const salFakeElements = document.querySelectorAll('*[data-fake-sal]');
    const salInEvent = new CustomEvent('sal:in');

    salContainers?.forEach(el => {
      window.requestAnimationFrame(() => el.classList.add('sal-animate'));
      el.setAttribute('data-fake-sal', 'true');
      el.addEventListener('transitionend', cleanupSal);
      el.dispatchEvent(salInEvent);
    });
    salElements?.forEach(el => {
      window.requestAnimationFrame(() => el.classList.add('sal-animate'));
      el.setAttribute('data-fake-sal', 'true');
      el.dispatchEvent(salInEvent);
      cleanupSalContainer(el as HTMLElement);
    });
    salFakeElements?.forEach(el => {
      el.classList.add('sal-animate');
      el.dispatchEvent(salInEvent);
    });
  }, dependencyArray);
}

/**
 * Updates observer with new elements to animate. Useful for dynamically injected HTML.
 *
 * @see https://github.com/mciastek/sal?tab=readme-ov-file#api
 */
export const forceSalAnimations = (): void => {
  sal().update();
};
